"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ArrowRight, ShieldCheck, SlidersHorizontal, FileText, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  heading: string;
  lead: string;
  secondaryCta: string;
  imageAlt: string;
  chipLabel: string;
  footerNote: string;
  footerLink: string;
  specStrip: Array<{
    label: string;
    value: string;
  }>;
}
const heroCopy: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Alloy Selection & Engineering",
    heading: "Find the right stainless steel wire mesh grade for your operating environment.",
    lead: "Match alloy composition to thermal load, chemical exposure, and mechanical stress. Our engineering team assists you in specifying the exact stainless steel grade for reliable, long-term filtration performance.",
    secondaryCta: "View material charts",
    imageAlt: "Macro close-up of stainless steel wire mesh showing weave geometry and metallic surface",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "Raw rolls, slit coils, stamped discs & cut panels",
    footerLink: "Alloy guide",
    specStrip: [{
      label: "Standard Grades",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Woven Range",
      value: "1 µm – 25 mm (1 – 635 Mesh)"
    }, {
      label: "Wire Diameters",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Reference Standards",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  zh: {
    categoryBadge: "材料工程与牌号选型",
    heading: "针对严苛工况环境匹配最佳不锈钢丝网材质牌号",
    lead: "综合权衡合金化学成分、工作温度载荷、化学腐蚀接触及机械抗拉强度。我们的工程团队协助您精确确定不锈钢规格，保障长效可靠的过滤使用寿命。",
    secondaryCta: "查看材料对照表",
    imageAlt: "不锈钢编织丝网微观织物几何与金属光泽特写",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "原卷、分条卷带、冲压滤圆片及定制切片",
    footerLink: "选型指南",
    specStrip: [{
      label: "标准牌号",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "编织精度范围",
      value: "1 µm – 25 mm (1 – 635 目)"
    }, {
      label: "丝径范围",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "执行标准",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  es: {
    categoryBadge: "Selección de aleación e ingeniería",
    heading: "Encuentre el grado de malla de alambre de acero inoxidable adecuado para su entorno operativo.",
    lead: "Ajuste la composición de la aleación a la carga térmica, la exposición química y la tensión mecánica. Nuestro equipo de ingeniería le ayuda a especificar la aleación exacta para un rendimiento de filtración confiable.",
    secondaryCta: "Ver tablas de materiales",
    imageAlt: "Primer plano de malla de alambre de acero inoxidable que muestra la geometría de tejido",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "Rollos, bobinas cortadas, discos estampados y paneles",
    footerLink: "Guía de aleaciones",
    specStrip: [{
      label: "Grados estándar",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Rango de tejido",
      value: "1 µm – 25 mm (1 – 635 Mesh)"
    }, {
      label: "Diámetros de alambre",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Normas de referencia",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  de: {
    categoryBadge: "Legierungsauswahl & Engineering",
    heading: "Bestimmen Sie die optimale Edelstahl-Drahtgewebe-Güte für Ihre Prozessbedingungen.",
    lead: "Stimmen Sie Legierungszusammensetzung auf thermische Last, chemische Beanspruchung und mechanische Spannungen ab. Unser Fachteam unterstützt Sie bei der Spezifikation für langlebige Filtrationsleistung.",
    secondaryCta: "Materialtabellen ansehen",
    imageAlt: "Detailaufnahme eines Edelstahl-Drahtgewebes mit Webgeometrie und metallischer Oberfläche",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "Rollen, Spaltbänder, Ronden und Zuschnitte",
    footerLink: "Legierungsleitfaden",
    specStrip: [{
      label: "Standardgüten",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Maschenbereich",
      value: "1 µm – 25 mm (1 – 635 Mesh)"
    }, {
      label: "Drahtdurchmesser",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Referenznormen",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  fr: {
    categoryBadge: "Sélection d'alliage et ingénierie",
    heading: "Trouvez la nuance de toile métallique en acier inoxydable adaptée à votre environnement opérationnel.",
    lead: "Associez la composition de l'alliage à la charge thermique, l'exposition chimique et la contrainte mécanique. Nos ingénieurs vous aident à spécifier la nuance exacte pour une filtration durable.",
    secondaryCta: "Consulter les fiches matières",
    imageAlt: "Gros plan macro d'une toile métallique en acier inoxydable montrant la géométrie de tissage",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "Rouleaux, bandes refendues, disques découpés et panneaux",
    footerLink: "Guide des nuances",
    specStrip: [{
      label: "Nuances standard",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Plage de filtration",
      value: "1 µm – 25 mm (1 – 635 Mesh)"
    }, {
      label: "Diamètres de fil",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Normes de référence",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  pt: {
    categoryBadge: "Seleção de liga e engenharia",
    heading: "Encontre o grau de tela de arame de aço inoxidável ideal para suas condições operacionais.",
    lead: "Compatibilize a composição da liga com carga térmica, exposição química e tensões mecânicas. Nossa equipe técnica auxilia na especificação precisa para desempenho contínuo de filtração.",
    secondaryCta: "Ver tabelas de materiais",
    imageAlt: "Close macro de tela de arame de aço inoxidável destacando a geometria do entrelaçamento",
    chipLabel: "AISI 304, 304L, 316, 316L",
    footerNote: "Rolos, fitas cortadas, discos estampados e painéis",
    footerLink: "Guia de ligas",
    specStrip: [{
      label: "Graus padrão",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Faixa de tecelagem",
      value: "1 µm – 25 mm (1 – 635 Mesh)"
    }, {
      label: "Diâmetros de arame",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Normas de referência",
      value: "ASTM E2016 / ISO 9044"
    }]
  }
};
export default function SelectionHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, heroCopy);
  return <section id="gradeselectionhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <Reveal className="lg:col-span-7 flex flex-col items-start">
            {/* Badges row: exactly two badges */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption text-foreground">
                <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                <span>{copy.categoryBadge}</span>
              </Badge>
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                <span>ASTM E2016 / ISO 9044</span>
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance mf-h1 text-foreground">
              {copy.heading}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
              {copy.lead}
            </p>

            {/* CTAs */}
            <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                <Link to="/MaterialsAndGradesPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" aria-hidden="true" />
                  <span>{copy.secondaryCta}</span>
                </Link>
              </Button>
            </div>
          </Reveal>

          {/* Right Column: Spec Display Card (5 Columns on lg) */}
          <Reveal delay={50} className="lg:col-span-5 w-full">
            <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
              {/* Product Visual Container */}
              <div className="mf-card-media relative mf-ratio-hero w-full">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-close-up-photograph-of-stainless-s-a05e55.webp" alt={copy.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.chipLabel}</span>
                </div>
              </div>

              {/* Single Footer Line */}
              <CardContent className="p-4">
                <div className="flex items-center justify-between mf-caption">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <CircleCheck className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                    <span className="truncate">{copy.footerNote}</span>
                  </div>
                  <Link to="/MaterialsAndSurfaceFinishesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                    <span>{copy.footerLink}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {copy.specStrip.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}